/**
 * Translation Test Page
 * 
 * Demonstrates how to use the translation system
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Container } from '../layout/Container';
import { HeaderSection } from '../layout/CardsContainer';
import Button from '../components/Button';
import useTranslation from '../hooks/useTranslation';
import axios from 'axios';
import { ENV_CONFIG } from '../config/environment-unified';

const TranslationTest = () => {
  const { t, currentLanguage, translateText, isTranslating } = useTranslation();
  const [apiResponse, setApiResponse] = useState('');
  const [translatedResponse, setTranslatedResponse] = useState('');

  // Test API call to see translation in action
  const testApiCall = async () => {
    try {
      setApiResponse('Loading...');
      setTranslatedResponse('');
      
      // Make a test API call (this will be intercepted and translated)
      const response = await axios.get(`${ENV_CONFIG.API_BASE_URL}/api/stream/games`);
      
      setApiResponse(JSON.stringify(response.data, null, 2));
      setTranslatedResponse('Response was automatically translated by axios interceptor');
    } catch (error) {
      setApiResponse(`Error: ${error.message}`);
    }
  };

  // Test dynamic translation
  const testDynamicTranslation = async () => {
    const frenchText = 'Ceci est un test de traduction dynamique. Le système devrait traduire ce texte automatiquement.';
    const translated = await translateText(frenchText);
    setTranslatedResponse(translated);
  };

  return (
    <Container style={{ paddingTop: '3rem', paddingBottom: '3rem', maxWidth: '900px' }}>
      <HeaderSection title="Translation System Test" />
      
      <Section>
        <SectionTitle>Current Language</SectionTitle>
        <InfoBox>
          <strong>Language:</strong> {currentLanguage.toUpperCase()}<br />
          <strong>Status:</strong> {isTranslating ? 'Translating...' : 'Ready'}
        </InfoBox>
      </Section>

      <Section>
        <SectionTitle>UI String Translation (from dictionary)</SectionTitle>
        <TranslationDemo>
          <TranslationItem>
            <Label>Key: "auth.login"</Label>
            <Result>{t('auth.login')}</Result>
          </TranslationItem>
          <TranslationItem>
            <Label>Key: "auth.signup"</Label>
            <Result>{t('auth.signup')}</Result>
          </TranslationItem>
          <TranslationItem>
            <Label>Key: "common.loading"</Label>
            <Result>{t('common.loading')}</Result>
          </TranslationItem>
          <TranslationItem>
            <Label>Key: "nav.tournaments"</Label>
            <Result>{t('nav.tournaments')}</Result>
          </TranslationItem>
          <TranslationItem>
            <Label>Key: "nav.events"</Label>
            <Result>{t('nav.events')}</Result>
          </TranslationItem>
          <TranslationItem>
            <Label>Key: "stream.live"</Label>
            <Result>{t('stream.live')}</Result>
          </TranslationItem>
        </TranslationDemo>
      </Section>

      <Section>
        <SectionTitle>Dynamic Text Translation</SectionTitle>
        <ButtonRow>
          <Button 
            variant="primary" 
            onClick={testDynamicTranslation}
            disabled={isTranslating}
          >
            Test Dynamic Translation
          </Button>
        </ButtonRow>
        {translatedResponse && (
          <InfoBox>
            <strong>Translated:</strong><br />
            {translatedResponse}
          </InfoBox>
        )}
      </Section>

      <Section>
        <SectionTitle>API Response Translation (Axios Interceptor)</SectionTitle>
        <InfoText>
          When you change language and make API calls, the axios interceptor will 
          automatically translate response fields like "message", "error", "description", etc.
        </InfoText>
        <ButtonRow>
          <Button 
            variant="primary" 
            onClick={testApiCall}
            disabled={isTranslating}
          >
            Test API Call
          </Button>
        </ButtonRow>
        {apiResponse && (
          <CodeBox>
            {apiResponse}
          </CodeBox>
        )}
      </Section>

      <Section>
        <SectionTitle>How to Use in Your Components</SectionTitle>
        <CodeBox>
{`// 1. Import the hook
import useTranslation from '../hooks/useTranslation';

// 2. Use it in your component
const MyComponent = () => {
  const { t, currentLanguage, translateText } = useTranslation();
  
  return (
    <div>
      {/* Translate UI strings from dictionary */}
      <h1>{t('common.welcome')}</h1>
      
      {/* Use with fallback */}
      <p>{t('some.key', 'Fallback text')}</p>
      
      {/* Check current language */}
      <p>Current: {currentLanguage}</p>
      
      {/* Translate dynamic text */}
      <button onClick={async () => {
        const translated = await translateText('Bonjour');
        console.log(translated);
      }}>
        Translate
      </button>
    </div>
  );
};`}
        </CodeBox>
      </Section>
    </Container>
  );
};

export default TranslationTest;

// Styled Components
const Section = styled.div`
  margin-bottom: 3rem;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.color.secondaryBg || '#f5f5f5'};
  border-radius: 8px;
`;

const SectionTitle = styled.h2`
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.color.primaryText};
  font-size: 1.5rem;
`;

const InfoBox = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => theme.color.background || '#fff'};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.color.border || '#ddd'};
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-word;
`;

const CodeBox = styled.pre`
  padding: 1rem;
  background-color: #1e1e1e;
  color: #d4d4d4;
  border-radius: 4px;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const TranslationDemo = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
`;

const TranslationItem = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => theme.color.background || '#fff'};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.color.border || '#ddd'};
`;

const Label = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.color.secondaryText || '#666'};
  margin-bottom: 0.5rem;
`;

const Result = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.color.primary || '#4CAF50'};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const InfoText = styled.p`
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.color.secondaryText || '#666'};
  line-height: 1.6;
`;

